import './section-list.scss';
import {Box, Typography} from '@mui/material';
import {useContext, useRef, useState, useEffect, ReactNode} from 'react';
import {AppContext} from '../../contexts/app/context';
import {AppContextType} from '../../contexts/app/types';
import {LoadingContent} from '../loading-content/loading-content';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {SectionListData} from '../../interfaces/section-list-data';
import {useOffice} from '../../contexts/office/context';
import {ScreenPaths} from '../../enums';
import {useLocation} from 'react-router-dom';

type SectionProps = {
  value: string;
  children: ReactNode;
};

const Section = ({value, children}: SectionProps) => {
  const [isPinned, setIsPinned] = useState<boolean>(false);
  const headerRef = useRef(null);
  const appContext: AppContextType = useContext(AppContext);
  const officeContext = useOffice();
  let location = useLocation();

  // Component mount
  useEffect(() => {
    // Create IntersectionObserver
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (!isPinned && entry.intersectionRatio === 1) {
          setIsPinned(true);
        } else if (isPinned && entry.intersectionRatio !== 1) {
          setIsPinned(false);
        }
      },
      {
        threshold: 1,
      }
    );

    // Observe
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    // Clean up
    return () => {
      if (headerRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(headerRef.current);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerRef]);

  return (
    <Box className="section-list-section">
      <Box
        ref={headerRef}
        className={`section-list-section-column-1 ${
          isPinned ? 'section-list-section-column-1-pinned' : undefined
        }`}
        sx={{
          top: `${
            officeContext.state.pendingTasksVisible &&
            !location.pathname.includes(ScreenPaths.Me_Notifications)
              ? appContext.state.currentThemeSettings.headerHeight +
                appContext.state.currentThemeSettings.pendingTasksHeight
              : appContext.state.currentThemeSettings.headerHeight
          }px`,
        }}
      >
        <Box className="section-list-section-colum-container">
          <Typography variant="h6">{value}</Typography>
        </Box>
      </Box>
      <Box className="section-list-section-column-2">
        <Box className="section-list-section-container">{children}</Box>
      </Box>
    </Box>
  );
};

type SectionListProps = {
  data: SectionListData<any>;
  isLoading: boolean;
  allLoaded: boolean;
  renderItem: (item: any) => React.ReactElement;
};

export const SectionList = ({data, isLoading, allLoaded, renderItem}: SectionListProps) => {
  const {t} = useTranslation();

  return (
    <Box className="section-list-container">
      {Object.keys(data).map((section) => {
        return data[section].length > 0 ? (
          <Section key={`section-list-section-${section}`} value={section}>
            {data[section].map((item) => renderItem(item))}
          </Section>
        ) : undefined;
      })}
      {isLoading && <LoadingContent marginTop="24px" />}
      {allLoaded && !isLoading && Object.keys(data).length > 0 && (
        <Typography variant="body2" className="section-list-footer">
          {t(translations.section_list.no_more_data)}
        </Typography>
      )}
      {allLoaded && !isLoading && Object.keys(data).length === 0 && (
        <Typography variant="body2" className="section-list-footer">
          {t(translations.section_list.without_data)}
        </Typography>
      )}
    </Box>
  );
};
